// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { Map } from '@/components/map';
import { MapMarker } from '@/components/map-marker';
import { Location } from '@/location/api/types';
import { Box, Typography, alpha, colors, styled } from '@mui/material';
import { uniqBy } from 'lodash-es';

const DEFAULT_EMPTY_STATE = 'No locations to display';

const Overlay = styled(Typography)(({ theme }) => ({
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 1,
  display: 'flex',
  position: 'absolute',
  textAlign: 'center',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(6),
  color: theme.palette.text.secondary,
  backgroundColor: alpha(theme.palette.background.paper, 0.8),
}));

type LocationData = Pick<Location, 'id' | 'latitude' | 'longitude'>;

interface Props<T> {
  locations: T[];
  emptyState?: string;
  getColor?: (location: T) => string;
}

export function ReportLocationMap<T extends LocationData>(props: Props<T>) {
  const locations = uniqBy(props.locations, 'id');
  const emptyState = props.emptyState ?? DEFAULT_EMPTY_STATE;

  return (
    <Box position="relative" width="100%" height="100%">
      <Map>
        {locations.map((location) => {
          const color =
            props.getColor === undefined
              ? colors.blueGrey[300]
              : props.getColor(location);

          return (
            <MapMarker
              key={location.id}
              size={24}
              color={color}
              latitude={Number(location.latitude)}
              longitude={Number(location.longitude)}
            />
          );
        })}
      </Map>
      {locations.length === 0 && <Overlay>{emptyState}</Overlay>}
    </Box>
  );
}
